const getStackTrace = (row) => {
  const obj = {};
  if (!Error.captureStackTrace) return "";
  Error.captureStackTrace(obj, getStackTrace);

  const [, source, line, column] =
    /https?:\/\/[^/]+\/([^:]+):([^:]+):([0-9]+)/im.exec(obj.stack.split("\n")[row]) || [];

  return !source ? obj.stack.split("\n")[row] : `${source} ${line}:${column} \n`;
};

export default (eventTimeOutError = 5000, debug = false) => {
  const contexts = debug ? new Set() : undefined;
  let nextContext = 0;
  function addEventHandler(context, mapevents, name) {
    const callbacks = {};
    const callbacksprio = {};
    const callbacksStack = {};
    if (typeof mapevents === "string") {
      name = mapevents;
      mapevents = undefined;
    }

    callbacksStack.addEventHandlerInitiator = [`${name} ${Math.random()}`, getStackTrace(2)];

    context = context || {};
    const emissions = debug ? new Map() : undefined;
    if (debug) {
      const id = ++nextContext;
      Object.defineProperty(context, "eventSnapshot", {
        configurable: true,
        value: () => ({
          id,
          name: name || "context",
          source: callbacksStack.addEventHandlerInitiator[1],
          events: [...new Set([...(mapevents || []), ...Object.keys(callbacks), ...emissions.keys()])].map((event) => ({
            name: event,
            handlers: (callbacks[event] || []).map((callback, index) => ({
              name: callback.name,
              source: callbacksStack[event][index],
              priority: callbacksprio[event][index],
            })),
            emitters: [...(emissions.get(event) || [])].map(([source, count]) => ({ source, count })),
          })),
        }),
      });
      contexts.add(new WeakRef(context));
    }

    const addEvent = (event, callback, prio) => {
      if (typeof callback === "number") {
        const tmp = callback;
        callback = prio;
        prio = tmp;
      }

      if (!callback) return console.error("No callback", getStackTrace(5));
      if (typeof callback !== "function") return console.error("Not a function", getStackTrace(5), callback);

      prio = prio || 0;

      if (!callbacks[event]) {
        callbacks[event] = [];
        callbacksprio[event] = [];
        callbacksStack[event] = [];
      }

      let pos = callbacks[event].length;
      while (pos && callbacksprio[event][pos - 1] < prio) pos--;

      callbacksprio[event].splice(pos, 0, prio);
      callbacks[event].splice(pos, 0, callback);
      callbacksStack[event].splice(pos, 0, getStackTrace(5));
    };

    context.on = (event, callback, prio) => {
      event = event instanceof Array ? event : [event];
      event.forEach((event) => addEvent(event, callback, prio));
    };

    const removeEvent = (event, callback) => {
      if (!callbacks[event]) {
        console.log("Missing event", event, callbacks);
        return;
      }
      const pos = callbacks[event].indexOf(callback);
      if (pos !== -1) {
        callbacks[event].splice(pos, 1);
        callbacksprio[event].splice(pos, 1);
        callbacksStack[event].splice(pos, 1);
      }

      if (!callbacks[event].length) delete callbacks[event];
    };

    context.off = (event, callback) => {
      event = event instanceof Array ? event : [event];
      event.forEach((event) => removeEvent(event, callback));
    };

    const printStack = (text) => text && text.replace("   at module.exports ", "at ");

    const fired = {};

    const doEvent = (event, data, source) => {
      if (debug) {
        source = source || getStackTrace(2);
        if (!emissions.has(event)) emissions.set(event, new Map());
        emissions.get(event).set(source, (emissions.get(event).get(source) || 0) + 1);
      }
      data = data || {};
      const callback = callbacks[event];

      if (!fired[event]) fired[event] = true;
      if (!callback) return Promise.resolve(data);

      return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          console.log(
            `💥 Event ${event} stopped ${pos} of ${
              callback.length
            } ${printStack(callbacksStack[event][pos - 1])} - ${callbacksStack[event]
              .map((item, index) => `${index}.${printStack(item)} `)
              .join("  ")}`,
          );
        }, eventTimeOutError);
        let pos = 0;
        let oldData = data;

        const next = (data) => {
          if (data instanceof Promise) {
            data.then(next).catch(reject);
            return;
          }

          data = typeof data === "undefined" ? oldData : data;

          if (pos >= callback.length) {
            clearTimeout(timer);
            resolve(data);
            return;
          }

          oldData = data;
          Promise.resolve(callback[pos++](data)).then(next).catch(reject);
        };
        next(data);
      });
    };
    context.event = doEvent;

    context.waitFor = (event) =>
      new Promise((resolve) => {
        const call = () => {
          context.off(event, call);
          resolve();
        };

        if (fired[event]) resolve();
        else context.on(event, call);
      });

    if (mapevents)
      mapevents.forEach((event) => {
        context[event] = (data, defectiveCallback) => {
          // if (defectiveCallback) debugger;
          return doEvent(event, data, debug ? getStackTrace(2) : undefined);
        };
      });

    return context;
  }
  if (debug)
    addEventHandler.inspectEvents = (app) => {
      const active = new Map();
      for (const reference of contexts) {
        const context = reference.deref();
        if (context) active.set(context, context.eventSnapshot());
        else contexts.delete(reference);
      }
      const paths = new Map([[app, "app"]]);
      const queue = [app];
      for (let index = 0; index < queue.length; index++) {
        const context = queue[index];
        for (const [key, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(context))) {
          if (!active.has(descriptor.value) || paths.has(descriptor.value)) continue;
          paths.set(
            descriptor.value,
            `${paths.get(context)}${/^[a-zA-Z_$][\w$]*$/.test(key) ? `.${key}` : `[${JSON.stringify(key)}]`}`,
          );
          queue.push(descriptor.value);
        }
      }
      return [...active].map(([context, snapshot]) => ({
        ...snapshot,
        path: paths.get(context) || `${snapshot.name} #${snapshot.id}`,
      }));
    };
  return addEventHandler;
};
